import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { matchSorter } from 'match-sorter';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { getUnitsByProjectId } from '@/data';
import {
  Project,
  Unit,
  Building,
  LeaseLifecycle,
} from '@urbanmix-tech/shared-js';
import { SearchMixTypeItem } from '@/components/GlobalSearchBar/SearchMixTypeItem';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useProjects } from '@/shared/hooks/useProjects';
import { twMerge } from 'tailwind-merge';
import ResultRow from './ResultRow';
import AppIcon from '../shared/AppIcon/AppIcon';

const GlobalSearchBar = () => {
  const [list, setList] = useState<SearchMixTypeItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const allowedProjects = useProjects();

  const mapProjectsToSearchMixType = (
    projects: Project[],
    units: Unit[]
  ): SearchMixTypeItem[] => {
    const searchMixItems: SearchMixTypeItem[] = [];

    const projectMap = new Map<string, Project>();
    const buildingMap = new Map<string, Building>();

    projects.forEach((project) => {
      projectMap.set(project.id, project);

      project.buildings.forEach((building) => {
        buildingMap.set(
          JSON.stringify({ projectId: project.id, buildingId: building.id }),
          building
        );
      });
    });

    units.forEach((unit) => {
      const project = projectMap.get(unit.projectId);
      const building = buildingMap.get(
        JSON.stringify({
          projectId: unit.projectId,
          buildingId: unit.buildingId,
        })
      );

      if (!project || !building) {
        return;
      }

      searchMixItems.push({
        id: unit.id,
        project: project.name,
        projectId: project.id,
        building: building.name,
        buildingId: building.id,
        unit: unit.name || unit.id,
        unitId: unit.id,
        br: unit.numBedrooms,
        baths: unit.numBathrooms,
        sqft: unit.sqft,
        leaseEndDate: dayjs(
          convertPseudoDateToRealDate(unit.leaseEndDate)
        ).format('MM/DD/YYYY'),
        marketRent: unit.marketRent,
        stage: Array.isArray(unit.currentState)
          ? unit.currentState.length > 0
            ? unit.currentState[unit.currentState.length - 1]
            : LeaseLifecycle.NOTICE
          : (unit.currentState as LeaseLifecycle) || LeaseLifecycle.NOTICE,
      });
    });

    return searchMixItems;
  };

  useEffect(() => {
    const fetcheData = async () => {
      try {
        const units: Unit[] = [];

        const promises = allowedProjects.map(async (project) =>
          getUnitsByProjectId(project.id)
        );

        await Promise.all(promises).then((values) => {
          units.push(...values.reduce((acc, item) => [...acc, ...item], []));
        });

        if (allowedProjects) {
          const result: SearchMixTypeItem[] = mapProjectsToSearchMixType(
            allowedProjects,
            units
          );
          setList(result);
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
      }
    };

    fetcheData();
  }, [allowedProjects]);

  const searchKeys = ['project', 'building', 'unit'];
  const gridHeader = [
    'Project',
    // 'Building',
    'Unit ID',
    // 'BR',
    // 'Baths',
    // 'SqFt',
    // 'Lease End',
    // 'Rent ($)',
    'Stage',
  ];

  const gridRows = [
    'project',
    // 'building',
    'unit',
    // 'br',
    // 'baths',
    // 'sqft',
    // 'leaseEndDate',
    // 'marketRent',
    'stage',
  ];

  function queryMatches(value: string, inputValue: string) {
    const matches = match(value, inputValue, {
      insideWords: true,
      findAllOccurrences: true,
    });
    return parse(value, matches);
  }

  return (
    <Autocomplete
      id="input-query"
      forcePopupIcon={false}
      clearOnEscape
      disablePortal
      filterOptions={(options, { inputValue }) => {
        const ret = matchSorter(options, inputValue || searchText, {
          keys: searchKeys,
        });
        return ret;
      }}
      options={list}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id && option.buildingId === value.buildingId
      }
      groupBy={() => '1'}
      renderGroup={(params) => (
        <ul
          className="flex min-w-[875px] flex-col gap-1 bg-transparent p-2"
          key={`group-${params.group}`}
        >
          <div
            key={`heade-${params.group}`}
            className="flex justify-between rounded-lg bg-zinc-800 pl-17 pr-43 text-sm py-2"
          >
            {gridHeader.map((item, key) => (
              <>
                {key > 0 && (
                  <span
                    className="my-3 w-1 bg-white/10"
                    key={`head-divider${item}`}
                  />
                )}
                <div
                  className="flex-1 text-center text-stone-400 "
                  key={`head-${item}`}
                >
                  {item}
                </div>
              </>
            ))}
          </div>
          {params.children}
        </ul>
      )}
      getOptionLabel={(option: SearchMixTypeItem) => option.unitId}
      componentsProps={{
        popper: {
          placement: 'bottom-end',
          className: 'min-w-[875px] bg-zinc-900 border border-white/10 rounded-lg mt-2',
          style: {
            width: 'fit-content',
          },
        },
      }}
      onChange={(_event, _value, reason) => {
        if (reason === 'clear') {
          setSearchText('');
        }
      }}
      renderOption={(props, option, { inputValue }) => {
        const parts: Record<
          string,
          Array<{ text: string; highlight: boolean }>
        > = {};

        const handleClick = () => {
          navigate(
            `/m/projects/${option.projectId}/${option.buildingId}/units/${option.unitId}`
          );
        };
        searchKeys.forEach((key) => {
          const value = option[key as keyof SearchMixTypeItem];
          if (value != null && typeof value === 'string') {
            parts[key] = queryMatches(value, inputValue);
          }
        });
        return (
          <li
            {...props}
            className="bg-transparent"
            key={`option-${option.projectId}-${option.id}`}
          >
            <div
              className="flex cursor-pointer justify-between rounded bg-zinc-800/70 pl-19 pr-20 text-sm hover:bg-zinc-800 text-stone-200 py-2 border border-white/5"
              onClick={handleClick}
            >
              {gridRows.map((keyItem, index) => (
                <ResultRow
                  key={`keyItem-${keyItem}`}
                  index={index}
                  keyItem={keyItem}
                  searchKeys={searchKeys}
                  parts={parts}
                  option={option}
                />
              ))}
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label=""
          variant="standard"
          placeholder="Search for projects, units, teams"
          className={twMerge(
            'flex h-36 w-[300px] items-center justify-center gap-16 rounded-lg bg-zinc-900/50 border border-white/10 px-12 leading-[14px] tracking-tight text-white'
          )}
          size="small"
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          onReset={() => setSearchText('')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon name="search" width={16} className="text-stone-400" />
              </InputAdornment>
            ),
            disableUnderline: true,
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            background: '#18181b',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
            padding: '0',
            marginTop: '8px',
          },
        },
      }}
      sx={{
        borderRadius: '8px',
        background: `transparent !important`,
        '& .MuiFormControl-root': { flex: 1, justifyContent: 'center' },
        '& input,': {
          fontSize: '14px',
          color: '#fff !important',
          fontWeight: 400,
          lineHeight: '14px',
          letterSpacing: '0.35px',
          background: `transparent !important`,
          marginTop: '2px',
        },
        '& input::placeholder': {
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '14px',
          letterSpacing: '0.35px',
          color: '#a1a1aa !important',
          background: `transparent !important`,
        },
      }}
    />
  );
};

export default GlobalSearchBar;
